import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarClientesDisponiveisEtiquetas, emitirEtiquetaAvulsa } from '~/Services/Service';
import SelectPaginado from './SelectPaginado';

const TIPO_BUSCA_CODIGO = 0;
const TIPO_BUSCA_BOLETO = 1;

const MODELOS_ETIQUETA = [
  { value: 'A5Q-50100', label: 'A5Q-50100 PIMACO' },
  { value: 'Q-50100', label: 'Q-50100 PIMACO' },
];

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const validationSchema = Yup.object().shape({
  codigo_grupo_empresa: Yup.number()
    .nullable()
    .required('Grupo de empresa é obrigatório.'),
  tipo_busca: Yup.number().required(),
  cliente: Yup.object()
    .nullable()
    .when('tipo_busca', {
      is: TIPO_BUSCA_CODIGO,
      then: Yup.object().nullable().required('Cliente é obrigatório.'),
    }),
  boleto: Yup.string().when('tipo_busca', {
    is: TIPO_BUSCA_BOLETO,
    then: Yup.string().required('Número do boleto é obrigatório.'),
  }),
  qtde_destinatario: Yup.number()
    .min(1, 'Mínimo 1.')
    .max(999, 'Máximo 999.')
    .required('Quantidade destinatário é obrigatória.'),
  qtde_remetente: Yup.number()
    .min(0, 'Mínimo 0.')
    .max(999, 'Máximo 999.')
    .required('Quantidade remetente é obrigatória.'),
  codigo_remetente: Yup.number()
    .nullable()
    .when('qtde_remetente', {
      is: (val) => val > 0,
      then: Yup.number().nullable().required('Remetente é obrigatório quando quantidade remetente > 0.'),
    }),
  modelo_folha: Yup.string()
    .nullable()
    .required('Padrão da etiqueta é obrigatório.'),
});

const ModalEtiquetaAvulsa = ({ show, onHide, gruposEmpresa, codigoGrupoEmpresaDefault, remetentes }) => {
  const [emitindo, setEmitindo] = useState(false);
  const [clientesOptions, setClientesOptions] = useState([]);
  const [carregandoClientes, setCarregandoClientes] = useState(false);

  const geDefault = codigoGrupoEmpresaDefault
    ? (gruposEmpresa || []).find((g) => g.value === codigoGrupoEmpresaDefault) || null
    : null;

  const remetentesOptions = (remetentes || []).map((r) => ({
    value: r.codigo,
    label: r.nome,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      codigo_grupo_empresa: codigoGrupoEmpresaDefault || null,
      ge_option: geDefault,
      tipo_busca: TIPO_BUSCA_CODIGO,
      cliente: null,
      boleto: '',
      qtde_destinatario: 1,
      qtde_remetente: 1,
      codigo_remetente: null,
      remetente_option: null,
      modelo_folha: null,
      modelo_option: null,
    },
    validationSchema,
    onSubmit: () => {},
  });

  // Carrega TODOS os clientes (Comércio) do GE selecionado — lista completa, sem busca
  // incremental. Fonte: UEtiqueta.pas:1618-1624 (clicomercio é lookup com a lista inteira).
  const geAtual = formik.values.codigo_grupo_empresa;
  useEffect(() => {
    let ativo = true;
    if (!geAtual) {
      setClientesOptions([]);
      return undefined;
    }
    setCarregandoClientes(true);
    listarClientesDisponiveisEtiquetas({
      busca: null,
      codigo_grupo_empresa: geAtual,
      pagina: 0,
      tamanho_pagina: 100000,
      tipo_emissao: 'comercio',
    })
      .then((data) => {
        if (!ativo) return;
        const lista = Array.isArray(data) ? data : (data && data.itens ? data.itens : []);
        setClientesOptions(
          lista.map((c) => ({
            value: c.codigo_conta,
            label: `${c.codigo_conta} — ${c.razao_social || c.apelido_conta || ''}`,
            ...c,
          }))
        );
      })
      .catch(() => {
        if (ativo) setClientesOptions([]);
      })
      .finally(() => {
        if (ativo) setCarregandoClientes(false);
      });
    return () => {
      ativo = false;
    };
  }, [geAtual]);

  const handleEmitir = useCallback(async () => {
    const errors = await formik.validateForm();
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );

    if (Object.keys(errors).length > 0) {
      return;
    }

    const { values } = formik;

    const payload = {
      codigo_grupo_empresa: values.codigo_grupo_empresa,
      codigo_conta: values.tipo_busca === TIPO_BUSCA_CODIGO && values.cliente ? values.cliente.value : null,
      boleto: values.tipo_busca === TIPO_BUSCA_BOLETO ? values.boleto : null,
      modelo_folha: values.modelo_folha,
      qtde_destinatario: values.qtde_destinatario,
      qtde_remetente: values.qtde_remetente,
      codigo_remetente: values.qtde_remetente > 0 ? values.codigo_remetente : null,
    };

    setEmitindo(true);
    try {
      const blob = await emitirEtiquetaAvulsa(payload);
      downloadBlob(blob, 'etiqueta-avulsa.pdf');
      toast.success('Etiqueta avulsa emitida.');
      formik.resetForm();
      onHide();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao emitir etiqueta.');
    } finally {
      setEmitindo(false);
    }
  }, [formik, onHide]);

  const handleHide = useCallback(() => {
    formik.resetForm();
    onHide();
  }, [formik, onHide]);

  const tipoBusca = formik.values.tipo_busca;

  return (
    <Modal show={show} onHide={handleHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Etiqueta Avulsa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Grupo de Empresa */}
          <Form.Group as={Row} controlId="ge">
            <Form.Label column sm={4}>
              Grupo de Empresa:
            </Form.Label>
            <Col sm={8}>
              <Select
                inputId="ge"
                options={gruposEmpresa || []}
                value={formik.values.ge_option}
                onChange={(option) => {
                  formik.setFieldValue('ge_option', option);
                  formik.setFieldValue('codigo_grupo_empresa', option ? option.value : null);
                  formik.setFieldValue('cliente', null);
                }}
                onBlur={() => formik.setFieldTouched('codigo_grupo_empresa', true)}
                placeholder="Selecione..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum grupo encontrado'}
              />
              {formik.touched.codigo_grupo_empresa && formik.errors.codigo_grupo_empresa && (
                <Form.Text className="text-danger">{formik.errors.codigo_grupo_empresa}</Form.Text>
              )}
            </Col>
          </Form.Group>

          {/* Tipo de Busca — RadioGroup1 */}
          <Form.Group as={Row}>
            <Form.Label column sm={4}>
              Buscar por:
            </Form.Label>
            <Col sm={8}>
              <Form.Check
                inline
                type="radio"
                id="tipo-busca-codigo"
                label="Código Cliente Comércio"
                value={TIPO_BUSCA_CODIGO}
                checked={tipoBusca === TIPO_BUSCA_CODIGO}
                onChange={() => {
                  formik.setFieldValue('tipo_busca', TIPO_BUSCA_CODIGO);
                  formik.setFieldValue('boleto', '');
                  formik.setFieldValue('cliente', null);
                }}
              />
              <Form.Check
                inline
                type="radio"
                id="tipo-busca-boleto"
                label="Boleto de Vendas"
                value={TIPO_BUSCA_BOLETO}
                checked={tipoBusca === TIPO_BUSCA_BOLETO}
                onChange={() => {
                  formik.setFieldValue('tipo_busca', TIPO_BUSCA_BOLETO);
                  formik.setFieldValue('cliente', null);
                  formik.setFieldValue('boleto', '');
                }}
              />
            </Col>
          </Form.Group>

          {/* Cliente (async-select) — apenas quando radio=0 */}
          {tipoBusca === TIPO_BUSCA_CODIGO && (
            <Form.Group as={Row} controlId="cliente">
              <Form.Label column sm={4}>
                Cliente:
              </Form.Label>
              <Col sm={8}>
                <SelectPaginado
                  inputId="cliente"
                  options={clientesOptions}
                  value={formik.values.cliente}
                  onChange={(option) => formik.setFieldValue('cliente', option)}
                  onBlur={() => formik.setFieldTouched('cliente', true)}
                  placeholder={carregandoClientes ? 'Carregando clientes...' : 'Selecione o cliente...'}
                  isLoading={carregandoClientes}
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  isClearable
                  noOptionsMessage={() =>
                    geAtual ? 'Nenhum cliente encontrado' : 'Selecione um GE primeiro'
                  }
                />
                {formik.touched.cliente && formik.errors.cliente && (
                  <Form.Text className="text-danger">{formik.errors.cliente}</Form.Text>
                )}
              </Col>
            </Form.Group>
          )}

          {/* Boleto — apenas quando radio=1 */}
          {tipoBusca === TIPO_BUSCA_BOLETO && (
            <Form.Group as={Row} controlId="boleto">
              <Form.Label column sm={4}>
                Nº do Boleto:
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  id="boleto"
                  type="text"
                  value={formik.values.boleto}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="boleto"
                  isInvalid={formik.touched.boleto && !!formik.errors.boleto}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.boleto}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          )}

          {/* Qtde Etiquetas Destinatário */}
          <Form.Group as={Row} controlId="qtde_destinatario">
            <Form.Label column sm={4}>
              Qtde Etiquetas Destinatário:
            </Form.Label>
            <Col sm={4}>
              <Form.Control
                id="qtde_destinatario"
                type="number"
                min={1}
                max={999}
                name="qtde_destinatario"
                value={formik.values.qtde_destinatario}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.qtde_destinatario && !!formik.errors.qtde_destinatario}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.qtde_destinatario}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>

          {/* Etiqueta Remetente + Qtde */}
          <Form.Group as={Row} controlId="remetente">
            <Form.Label column sm={4}>
              Etiqueta Remetente:
            </Form.Label>
            <Col sm={8}>
              <Select
                inputId="remetente"
                options={remetentesOptions}
                value={formik.values.remetente_option}
                onChange={(option) => {
                  formik.setFieldValue('remetente_option', option);
                  formik.setFieldValue('codigo_remetente', option ? option.value : null);
                }}
                onBlur={() => formik.setFieldTouched('codigo_remetente', true)}
                placeholder="Selecione..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum remetente encontrado'}
              />
              {formik.touched.codigo_remetente && formik.errors.codigo_remetente && (
                <Form.Text className="text-danger">{formik.errors.codigo_remetente}</Form.Text>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="qtde_remetente">
            <Form.Label column sm={4}>
              Qtde Etiquetas Remetente:
            </Form.Label>
            <Col sm={4}>
              <Form.Control
                id="qtde_remetente"
                type="number"
                min={0}
                max={999}
                name="qtde_remetente"
                value={formik.values.qtde_remetente}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.qtde_remetente && !!formik.errors.qtde_remetente}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.qtde_remetente}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>

          {/* Padrão da Etiqueta */}
          <Form.Group as={Row} controlId="modelo_folha">
            <Form.Label column sm={4}>
              Padrão da Etiqueta:
            </Form.Label>
            <Col sm={8}>
              <Select
                inputId="modelo_folha"
                options={MODELOS_ETIQUETA}
                value={formik.values.modelo_option}
                onChange={(option) => {
                  formik.setFieldValue('modelo_option', option);
                  formik.setFieldValue('modelo_folha', option ? option.value : null);
                }}
                onBlur={() => formik.setFieldTouched('modelo_folha', true)}
                placeholder="Selecione..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                noOptionsMessage={() => 'Nenhum modelo encontrado'}
              />
              {formik.touched.modelo_folha && formik.errors.modelo_folha && (
                <Form.Text className="text-danger">{formik.errors.modelo_folha}</Form.Text>
              )}
            </Col>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>
          Fechar
        </Button>
        <Button variant="primary" onClick={handleEmitir} disabled={emitindo}>
          {emitindo ? 'Emitindo...' : 'OK'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalEtiquetaAvulsa;
